import { useTranslation } from 'react-i18next';
import { type FileRejection } from '@mantine/dropzone';
import clsx from 'clsx';

import { fileSizeFormatter } from '../../utils/fileSizeFormatter';
import getFileExtensions from '../../utils/getFileExtensions';
import { mapMimeTypeToFileIcon } from '../../utils/mapMimeTypeToFileIcon';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';
import { Tooltip } from '../tooltip/Tooltip';

import { type FileSelection } from './fileUploadState';
import { FileData, isFileSelection } from './uploadTypes';

import styles from './upload-file.module.scss';

export type Props = {
  fileData: FileData;
  onRemoveFile?: (file: FileData) => void;
  disabled?: boolean;
  uploadFile?: (file: FileSelection) => Promise<void>;
  metaDataExtractionInput?: (fileData: FileSelection) => React.ReactNode;
};

type FileRejectionProps = {
  fileData: FileRejection;
  onRemoveFile?: (file: FileData) => void;
};

function FileRejection({ fileData, onRemoveFile }: FileRejectionProps) {
  const errorMessage = (fileData.errors[0]?.message ?? '')
    .replace(
      /\b\S+\/\S+\b/g,
      // Replace all mentions of MIME types with their corresponding file extensions
      (mime) => {
        const extensions = getFileExtensions([mime]);
        return extensions.length > 0 ? extensions.join(', ') : mime;
      }
    )
    .replace(
      /(\d+)\s*bytes/g,
      (_, bytes) => fileSizeFormatter(Number(bytes)) // Convert Bytes to MB
    );

  return (
    <div className={styles['divider']}>
      <div className={clsx(styles['root'], styles['error'])}>
        <div className={styles['section']}>
          <Tooltip label={errorMessage}>
            <GoogleFontIcon className={styles['error']} name="cancel" solid />
          </Tooltip>
          <TextWithTooltip
            textProps={{
              className: clsx(styles['file-name'], styles['rejected']),
              lineClamp: 1,
            }}
            tooltipProps={{ usePortal: false }}
          >
            {fileData.file.name}
          </TextWithTooltip>
        </div>

        <div className={styles['section']}>
          <div className={styles['file-size']}>
            {fileSizeFormatter(fileData.file.size)}
          </div>

          {onRemoveFile && (
            <div className={styles['actions']}>
              <Button
                variant="text"
                icon={<SVGIcon name="delete" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile(fileData);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type FileSelectionProps = {
  fileData: FileSelection;
  onRemoveFile?: (file: FileData) => void;
  metaDataExtractionInput?: (fileData: FileSelection) => React.ReactNode;
};

function FileSelection({
  fileData,
  onRemoveFile,
  metaDataExtractionInput,
}: FileSelectionProps) {
  return (
    <div
      className={clsx(styles['root'], {
        [styles['root-with-meta-data']]: metaDataExtractionInput,
      })}
    >
      <div className={styles['section']}>
        <SVGIcon name={mapMimeTypeToFileIcon(fileData.file.type)} />
        <TextWithTooltip
          textProps={{
            className: styles['file-name'],
            lineClamp: 1,
          }}
          tooltipProps={{ usePortal: false }}
        >
          {fileData.file.name}
        </TextWithTooltip>
      </div>

      {metaDataExtractionInput && (
        <div className={styles['section']}>
          {metaDataExtractionInput(fileData)}
        </div>
      )}

      <div className={styles['section']}>
        <div className={styles['file-size']}>
          {fileSizeFormatter(fileData.file.size)}
        </div>

        {onRemoveFile && (
          <div className={styles['actions']}>
            <Button
              variant="text"
              icon={<SVGIcon name="delete" />}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFile(fileData);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function UploadFile({
  fileData,
  onRemoveFile,
  uploadFile,
  metaDataExtractionInput,
}: Props) {
  const { t } = useTranslation();

  const isRejected = !isFileSelection(fileData);
  if (isRejected) {
    return <FileRejection fileData={fileData} onRemoveFile={onRemoveFile} />;
  }

  if (fileData.uploadState === 'initial') {
    return (
      <FileSelection
        fileData={fileData}
        onRemoveFile={onRemoveFile}
        metaDataExtractionInput={metaDataExtractionInput}
      />
    );
  }

  return (
    <div
      className={clsx(styles['root'], {
        [styles['root-with-meta-data']]: !!metaDataExtractionInput,
      })}
    >
      <div className={styles['section']}>
        <SVGIcon name={mapMimeTypeToFileIcon(fileData.file.type)} />
        <TextWithTooltip
          textProps={{
            className: styles['file-name'],
            lineClamp: 1,
          }}
          tooltipProps={{ usePortal: false }}
        >
          {fileData.file.name}
        </TextWithTooltip>
      </div>

      {metaDataExtractionInput && (
        <div className={styles['section']}>
          {metaDataExtractionInput(fileData)}
        </div>
      )}

      <div className={styles['section']}>
        {fileData.uploadState === 'isError' && (
          <div className={styles['error-text']}>
            {t('app.upload.uploadFailed')}
          </div>
        )}
        <div className={styles['file-size']}>
          {fileSizeFormatter(fileData.file.size)}
        </div>
        <div className={styles['actions']}>
          {fileData.uploadState === 'isSuccess' && (
            <GoogleFontIcon
              name="check_circle"
              solid
              className={styles['success']}
            />
          )}
          {fileData.uploadState === 'isPending' && (
            <LoadingIndicator className={styles['pending']} />
          )}
          {fileData.uploadState === 'isError' && (
            <>
              {uploadFile && (
                <Button
                  variant="text"
                  icon={<SVGIcon name="retry" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    uploadFile?.(fileData);
                  }}
                />
              )}
              {onRemoveFile && (
                <Button
                  variant="text"
                  icon={<SVGIcon name="delete" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(fileData);
                  }}
                />
              )}
              <Tooltip label={fileData.error}>
                <GoogleFontIcon
                  className={styles['error']}
                  name="cancel"
                  solid
                />
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
